// 登入裝置標籤 device_label（restful-api 02 §1.5 / 03 §12-D）。1:1 對應 iOS LSDeviceLabel /
// Android LSDeviceLabel。
//
// 這是**給人看的標籤**，不是識別子：不要放任何穩定 id（session id、fingerprint、帳號）——
// 這欄會存進 refresh_tokens 並抄進 §1.8 session.revoke 的 audit meta，放識別子等於憑空造出
// 一個跨 session 的追蹤鍵。瀏覽器沒有「使用者自己取的機器名」可用，所以退而求其次由 UA 推出
// 「Chrome on macOS」這種人看得懂、且**多台同型機器會撞名**的粗粒度描述 —— 撞名是可接受的，
// 冒充識別子不是。

/** 與伺服端 `maxDeviceLabelRunes` 同值。改這個數字前先改伺服端。 */
export const MAX_DEVICE_LABEL_CODE_POINTS = 64;

/** Unicode Cc（U+0000–001F / U+007F–009F）。伺服端用 Go 的 `unicode.IsControl`，它只涵蓋 Cc、
 *  **不含 Cf** —— 所以 ZWJ 不能剝，否則 emoji 序列被拆散且與伺服端存下來的字串不一致。 */
// eslint-disable-next-line no-control-regex
const CC = /[\u0000-\u001F\u007F-\u009F]/g;

/** 截到最多 `limit` 個 code point，且不切開 grapheme cluster。 */
function clamp(s: string, limit = MAX_DEVICE_LABEL_CODE_POINTS): string {
  if ([...s].length <= limit) return s;
  // Intl.Segmenter 才知道 grapheme 邊界（emoji ZWJ 序列 / 組合附加符號）。本專案的 TS lib
  // 目標還沒帶 es2022.intl 的型別，所以在此就地宣告最小介面（不動全域 tsconfig）；
  // 舊環境沒有它就退回 code point 切法 —— 仍然不會切壞 surrogate pair（[...s] 走 code point）。
  type GraphemeSegmenter = { segment(input: string): Iterable<{ segment: string }> };
  type SegmenterCtor = new (
    locales: string | undefined,
    options: { granularity: 'grapheme' }
  ) => GraphemeSegmenter;
  const Seg = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
  if (!Seg) return [...s].slice(0, limit).join('');
  let out = '';
  let used = 0;
  for (const { segment } of new Seg(undefined, { granularity: 'grapheme' }).segment(s)) {
    const w = [...segment].length;
    if (used + w > limit) break;
    out += segment;
    used += w;
  }
  return out;
}

/**
 * 正規化候選標籤；無可用內容回 `null`（語意 =「不要送這個欄位」）。
 * 與伺服端 `service.SanitizeDeviceLabel` 逐條對齊：剝 Cc → trim → 以 **code point**（= Go rune）
 * 截到 64，且停在 grapheme 邊界（結果必 ≤ 上限 ⇒ 伺服端不會再截一次）。
 */
export function sanitizeDeviceLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = clamp(raw.replace(CC, '').trim()).trim();
  return cleaned === '' ? null : cleaned;
}

const BROWSERS: Array<[RegExp, string]> = [
  // 順序有意義：Edge/Opera/Brave 的 UA 都含 "Chrome"，Chrome 的 UA 含 "Safari"。
  [/\bEdg[A-Z]?\//, 'Edge'],
  [/\bOPR\//, 'Opera'],
  [/\bFirefox\//, 'Firefox'],
  [/\bChrome\//, 'Chrome'],
  [/\bSafari\//, 'Safari'],
];

const PLATFORMS: Array<[RegExp, string]> = [
  [/\bWindows\b/, 'Windows'],
  [/\b(iPhone|iPad|iPod)\b/, 'iOS'],
  [/\bAndroid\b/, 'Android'],
  [/\bMac OS X\b|\bMacintosh\b/, 'macOS'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bLinux\b/, 'Linux'],
];

function match(ua: string, table: Array<[RegExp, string]>): string | null {
  for (const [re, name] of table) if (re.test(ua)) return name;
  return null;
}

/**
 * 由 User-Agent 推出人看得懂的裝置描述，例如 `Chrome on macOS`。
 * 兩者都認不出來就回 `null` —— 送一串原始 UA 進去只是把噪音存進資料庫，
 * 而 `null` 有既定的顯示後備（平台標籤 + IP/時間）。
 */
export function detectDeviceLabel(ua: string = navigator.userAgent): string | null {
  const browser = match(ua, BROWSERS);
  const platform = match(ua, PLATFORMS);
  if (browser && platform) return sanitizeDeviceLabel(`${browser} on ${platform}`);
  return sanitizeDeviceLabel(browser ?? platform);
}
